import api from "./api";
import { ok, isoUtc, reasonOf, fileToBase64, downloadFrom } from "./core";

export const IMPORT_TYPES = {
  EMPLOYEE: { label: "employees", title: "Import employees" },
  KRA_KPI: { label: "KRAs and KPIs", title: "Import KRA & KPI library" },
  // [NEW] PM-4 (REACT_APP_BACKEND_CYCLE_IMPORT=true): cycles are created as Draft by the backend import.
  PERFORMANCE_CYCLE: { label: "performance cycles", title: "Import performance cycles" },
};

const MAX_BYTES = 5 * 1000 * 1000;

async function download(url) {
  try {
    return await downloadFrom(url);
  } catch (err) {
    const body = err?.response?.data;
    if (body instanceof Blob) {
      try { err.response.data = JSON.parse(await body.text()); } catch {  }
    }
    throw err;
  }
}

const toBatch = (b) => ({
  id: b.import_batch_id,
  type: b.import_type,
  fileName: b.original_filename,
  format: b.file_format,
  status: b.batch_status,
  total: b.total_rows,
  valid: b.valid_rows,
  invalid: b.error_rows,
  expired: Boolean(b.expired),
  uploadedAt: isoUtc(b.uploaded_at),
  committedAt: isoUtc(b.committed_at),
  ignoredColumns: b.result_summary?.ignored_columns || [],
  summary: b.result_summary || {},
  preview: (b.preview || []).map(toRow),
});

const toRow = (r) => ({ rowNo: r.row_no, status: r.row_status, data: r.data || {}, normalized: r.normalized || {}, errors: r.errors || [] });

export const downloadImportTemplate = (type, format = "xlsx") => download(`/import-templates/${type}?format=${format}`);

export async function uploadImportFile(type, file, reason) {
  const name = file?.name || "";
  if (!/\.(csv|xlsx)$/i.test(name)) throw new Error("Choose a .csv or .xlsx file (download the template if unsure).");
  if (file.size > MAX_BYTES) throw new Error("The file is larger than 5 MB. Split it into smaller files.");
  const { data } = await api.post("/import-batches", {
    import_type: type,
    filename: name,
    content_base64: await fileToBase64(file),
    reason: reasonOf(reason, `Upload ${name} for validation`),
  });
  return ok(toBatch(data));
}

export async function getImportBatch(batchId) {
  const { data } = await api.get(`/import-batches/${batchId}`);
  return ok(toBatch(data));
}

export async function getImportRows(batchId, status, { limit = 500, offset = 0 } = {}) {
  const { data } = await api.get(`/import-batches/${batchId}/rows`, { params: { status, limit, offset } });
  return ok(data.map(toRow));
}

export const downloadImportErrors = (batchId, format = "xlsx") => download(`/import-batches/${batchId}/error-report?format=${format}`);

export async function commitImport(batchId, reason) {
  const { data } = await api.post(`/import-batches/${batchId}/commit`, { reason: reasonOf(reason, "Import validated rows") });
  return ok(toBatch(data));
}

export async function cancelImport(batchId, reason) {
  const { data } = await api.post(`/import-batches/${batchId}/cancel`, { reason: reasonOf(reason, "Import discarded") });
  return ok(toBatch(data));
}

export function exportEmployeeMaster({ status, departmentId, search } = {}, format = "xlsx") {
  const params = new URLSearchParams({ format });
  if (status) params.set("status", String(status).toUpperCase());
  if (departmentId) params.set("department_id", departmentId);
  if (search) params.set("search", search);
  return download(`/employee-master/export?${params.toString()}`);
}
